import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Label,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { auditService } from "../../services/admin";
import type { AuditFilters } from "../../types/admin";

export default function AdminAuditLogs() {
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [filters, setFilters] = useState<AuditFilters>({});

	const { data: logsData, isLoading } = useQuery({
		queryKey: ["admin-audit-logs", page, limit, filters],
		queryFn: () => auditService.getPlatformAuditLogs(page, limit, filters),
	});

	const logs = logsData?.data || [];
	const meta = logsData?.meta;

	const getStatusBadge = (status: string) => {
		const variants: Record<string, string> = {
			success: "bg-green-500/20 text-green-600 border-green-500/30",
			failed: "bg-red-500/20 text-red-600 border-red-500/30",
			unauthorized: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
			rate_limited: "bg-orange-500/20 text-orange-600 border-orange-500/30",
		};
		return (
			variants[status] || "bg-gray-500/20 text-gray-600 border-gray-500/30"
		);
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Audit Logs
					</h1>
					<p className="text-muted-foreground mt-2">
						Platform-wide audit logs and admin actions
					</p>
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Status</Label>
							<Select
								value={filters.status || "all"}
								onValueChange={(value) => {
									setFilters({
										...filters,
										status: value === "all" ? undefined : (value as any),
									});
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value="success">Success</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
									<SelectItem value="unauthorized">Unauthorized</SelectItem>
									<SelectItem value="rate_limited">Rate Limited</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Action</Label>
							<Input
								placeholder="Filter by action..."
								value={filters.action || ""}
								onChange={(e) => {
									setFilters({ ...filters, action: e.target.value });
									setPage(1);
								}}
							/>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">
								Resource Type
							</Label>
							<Input
								placeholder="Filter by resource..."
								value={filters.resourceType || ""}
								onChange={(e) => {
									setFilters({ ...filters, resourceType: e.target.value });
									setPage(1);
								}}
							/>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Search</Label>
							<Input
								placeholder="Search logs..."
								value={filters.search || ""}
								onChange={(e) => {
									setFilters({ ...filters, search: e.target.value });
									setPage(1);
								}}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Audit Logs Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						Audit Logs {meta && `(${meta.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<AuditLogsTableSkeleton />
					) : (
						<>
							<ResponsiveTable
								data={logs}
								keyExtractor={(log) => log.id}
								columns={[
									{
										key: "user",
										header: "User",
										mobileLabel: "User",
										render: (log) => (
											<span className="text-sm">
												{log.userEmail || log.userId || "System"}
											</span>
										),
									},
									{
										key: "action",
										header: "Action",
										mobileLabel: "Action",
										render: (log) => (
											<span className="text-sm font-medium">{log.action}</span>
										),
									},
									{
										key: "resource",
										header: "Resource",
										mobileLabel: "Resource",
										render: (log) => (
											<div>
												<p className="text-sm">{log.resourceType || "—"}</p>
												{log.resourceId && (
													<p className="text-xs text-muted-foreground font-mono">
														{log.resourceId.slice(0, 8)}...
													</p>
												)}
											</div>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (log) => (
											<Badge
												variant="outline"
												className={getStatusBadge(log.status)}
											>
												{log.status}
											</Badge>
										),
									},
									{
										key: "ipAddress",
										header: "IP Address",
										mobileLabel: "IP",
										hideOnMobile: true,
										render: (log) => (
											<span className="text-sm font-mono">
												{log.ipAddress || "—"}
											</span>
										),
									},
									{
										key: "created",
										header: "Created",
										mobileLabel: "Created",
										render: (log) => (
											<span className="text-sm">
												{new Date(log.createdAt).toLocaleString()}
											</span>
										),
									},
								]}
								emptyMessage="No audit logs found matching your filters."
							/>

							{/* Pagination */}
							{meta && meta.totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {meta.page} of {meta.totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={!meta.hasPrev}
										>
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(meta.totalPages, p + 1))
											}
											disabled={!meta.hasNext}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function AuditLogsTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}
